import React from 'react';
import { Box, Divider, Link, Stack, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useTranslation } from 'react-i18next';
import { formatCommissionAmount } from '../../models/Subscription';

export interface BookingStripeFeeBreakdownProps {
  /** Booking total (gross) used to derive host/tutor net when currencies match. */
  bookingTotalAmount?: number | null;
  /** Commission-rate snapshot at booking creation. */
  bookingCommissionPercent?: number | null;
  /** Platform commission snapshot at booking creation. */
  bookingCommissionAmount?: number | null;
  /** Actual Stripe processing fee — never derive client-side. */
  stripeProcessingFeeAmount?: number | null;
  /** Currency of the Stripe processing fee; may differ from booking currency. */
  stripeProcessingFeeCurrency?: string | null;
  /**
   * Stripe-hosted receipt URL. Bearer-style — open in a new tab only from an
   * authorized booking context; do not log or share publicly.
   */
  stripeReceiptUrl?: string | null;
  /** Booking currency used to format platform commission and net. */
  bookingCurrencyCode: string;
  /**
   * When false, only the receipt action is shown (customer booking detail).
   * Staff/host views show Net + fee tooltip and never the receipt link.
   */
  showStaffFees?: boolean;
}

function FeeRow({
  label,
  value,
  emphasize = false,
  muted = false,
}: {
  label: string;
  value: string;
  emphasize?: boolean;
  muted?: boolean;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      justifyContent="space-between"
      alignItems="baseline"
      sx={{ minWidth: 220 }}
    >
      <Typography
        variant="caption"
        component="span"
        sx={{
          opacity: muted ? 0.75 : 0.9,
          fontWeight: emphasize ? 700 : 500,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="caption"
        component="span"
        sx={{
          fontWeight: emphasize ? 700 : 600,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
          opacity: muted ? 0.75 : 1,
        }}
      >
        {value}
      </Typography>
    </Stack>
  );
}

/**
 * Booking payment fee captions and optional Stripe receipt action.
 * Staff/host view shows Net under the gross total, with a structured tooltip breakdown.
 * Customer view shows the Stripe receipt link only (no staff fee captions).
 * Renders recorded snapshots only — null processing fee means unavailable, not zero.
 * Net is only computed when the Stripe fee currency matches the booking currency.
 */
const BookingStripeFeeBreakdown: React.FC<BookingStripeFeeBreakdownProps> = ({
  bookingTotalAmount,
  bookingCommissionPercent,
  bookingCommissionAmount,
  stripeProcessingFeeAmount,
  stripeProcessingFeeCurrency,
  stripeReceiptUrl,
  bookingCurrencyCode,
  showStaffFees = true,
}) => {
  const { t, i18n } = useTranslation();

  const receiptUrl = !showStaffFees ? stripeReceiptUrl?.trim() || null : null;
  const processingFeeCurrency = (
    stripeProcessingFeeCurrency || bookingCurrencyCode
  ).toUpperCase();
  const bookingCurrency = bookingCurrencyCode.toUpperCase();
  const sameCurrencyFee =
    stripeProcessingFeeAmount != null &&
    processingFeeCurrency === bookingCurrency;

  const canShowNet =
    bookingTotalAmount != null &&
    Number.isFinite(bookingTotalAmount) &&
    sameCurrencyFee;

  const netAmount = canShowNet
    ? bookingTotalAmount -
      (bookingCommissionAmount ?? 0) -
      (stripeProcessingFeeAmount as number)
    : null;

  if (!showStaffFees && !receiptUrl) {
    return null;
  }

  const formatMoney = (amount: number, currency = bookingCurrencyCode) =>
    formatCommissionAmount(amount, currency, i18n.language);

  const commissionPercentLabel =
    bookingCommissionPercent != null
      ? t('bookingFees.commissionPercentShort', '{{percent}}%', {
          percent: new Intl.NumberFormat(i18n.language, {
            maximumFractionDigits: 2,
          }).format(bookingCommissionPercent),
        })
      : null;

  const platformCommissionLabel = commissionPercentLabel
    ? t(
        'bookingFees.platformCommissionWithPercent',
        'Platform commission ({{percent}})',
        { percent: commissionPercentLabel },
      )
    : t('bookingFees.platformCommission', 'Platform commission');

  const feeBreakdownTooltip = (
    <Stack spacing={0.75} sx={{ py: 0.5, px: 0.25 }}>
      {bookingTotalAmount != null && Number.isFinite(bookingTotalAmount) ? (
        <FeeRow
          label={t('bookingFees.gross', 'Gross')}
          value={formatMoney(bookingTotalAmount)}
        />
      ) : null}

      {bookingCommissionAmount != null ? (
        <FeeRow
          label={platformCommissionLabel}
          value={`−${formatMoney(bookingCommissionAmount)}`}
        />
      ) : null}

      {stripeProcessingFeeAmount != null ? (
        <FeeRow
          label={t('bookingFees.stripeProcessingFeeLabel', 'Stripe processing fee')}
          value={`−${formatMoney(
            stripeProcessingFeeAmount,
            stripeProcessingFeeCurrency || bookingCurrencyCode,
          )}`}
        />
      ) : (
        <FeeRow
          label={t('bookingFees.stripeProcessingFeeLabel', 'Stripe processing fee')}
          value={t(
            'bookingFees.stripeProcessingFeeUnavailableShort',
            'Not available',
          )}
          muted
        />
      )}

      <Divider sx={{ borderColor: 'currentColor', opacity: 0.22, my: 0.25 }} />

      <FeeRow
        label={t('bookingFees.net', 'Net')}
        value={
          netAmount != null
            ? formatMoney(netAmount)
            : t('bookingFees.netAmountUnavailableShort', 'Unavailable')
        }
        emphasize
        muted={netAmount == null}
      />
    </Stack>
  );

  return (
    <Stack spacing={0.25} alignItems="flex-end">
      {showStaffFees ? (
        <Tooltip title={feeBreakdownTooltip} arrow enterDelay={200} describeChild>
          <Box
            component="span"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.35,
              width: 'fit-content',
              cursor: 'help',
              color: 'text.secondary',
            }}
          >
            <InfoOutlinedIcon sx={{ fontSize: 13, opacity: 0.8 }} />
            <Typography variant="caption" color="inherit" component="span" sx={{ lineHeight: 1.2 }}>
              {netAmount != null
                ? t('bookingFees.netAmount', 'Net {{amount}}', {
                    amount: formatMoney(netAmount),
                  })
                : t('bookingFees.netAmountUnavailable', 'Net unavailable')}
            </Typography>
          </Box>
        </Tooltip>
      ) : null}
      {receiptUrl ? (
        <Link
          href={receiptUrl}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          underline="hover"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            width: 'fit-content',
            fontWeight: 600,
          }}
        >
          {t('bookingFees.viewStripeReceipt', 'View Receipt')}
          <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
      ) : null}
    </Stack>
  );
};

export default BookingStripeFeeBreakdown;
